import {
  COMMENTARY_SNAPSHOT,
  COMMENTARY_SNAPSHOT_ERROR,
  type ChangeAuditEntry,
} from "@/api/changeAudit.service";
import { languageName } from "../Cms/utils/languageName";
import { ChangeValue, FromTo } from "./auditDetailValues";
import styles from "./AuditLog.module.css";
import { researchFieldLabel, researchTableLabel } from "./researchLabels";

function SingleValue({
  field,
  value,
  side,
}: {
  field: string;
  value: unknown;
  side: "from" | "to";
}) {
  return (
    <div className={styles.change}>
      <h4 className={styles.changeField}>{field}</h4>
      <div className={styles.valueBlock}>
        <span className={styles.valueLabel}>{side === "from" ? "From" : "To"}</span>
        <ChangeValue value={value} side={side} />
      </div>
    </div>
  );
}

function ResearchChanges({ entry }: { entry: ChangeAuditEntry }) {
  const rowValues = Object.entries(entry.changedFields ?? {});
  const changes = entry.changes ? Object.entries(entry.changes) : [];
  const heading =
    entry.action === "INSERT"
      ? "Values set"
      : entry.action === "DELETE"
        ? "Values removed"
        : "Changes";

  return (
    <section aria-labelledby="audit-changes-heading">
      <h3 id="audit-changes-heading" className={styles.sectionHeading}>
        {heading}
      </h3>
      {entry.action === "UPDATE" ? (
        changes.length === 0 ? (
          <p className={styles.muted}>This entry records no field changes.</p>
        ) : (
          changes.map(([field, change]) => (
            <FromTo
              key={field}
              field={researchFieldLabel(field)}
              from={change.from}
              to={change.to}
            />
          ))
        )
      ) : rowValues.length === 0 ? (
        <p className={styles.muted}>This entry records no field values.</p>
      ) : (
        rowValues.map(([field, value]) => (
          <SingleValue
            key={field}
            field={researchFieldLabel(field)}
            value={value}
            side={entry.action === "DELETE" ? "from" : "to"}
          />
        ))
      )}
    </section>
  );
}

function CommentarySnapshot({ changedFields }: { changedFields: Record<string, unknown> }) {
  const error = changedFields[COMMENTARY_SNAPSHOT_ERROR];
  const snapshot = changedFields[COMMENTARY_SNAPSHOT];
  if (error === undefined && snapshot === undefined) return null;

  const languages = Object.entries((snapshot ?? {}) as Record<string, Record<string, unknown>>);

  return (
    <section aria-labelledby="audit-commentary-heading">
      <h3 id="audit-commentary-heading" className={styles.sectionHeading}>
        Commentary at this point
      </h3>
      {typeof error === "string" && error !== "" ? (
        <p className={styles.muted}>Commentary snapshot unavailable (error {error})</p>
      ) : languages.length === 0 ? (
        <p className={styles.muted}>No commentary</p>
      ) : (
        languages.map(([language, fields]) => (
          <div key={language} className={styles.change}>
            <h4 className={styles.changeField}>{languageName(language)}</h4>
            {Object.entries(fields ?? {}).map(([field, value]) => (
              <div key={field} className={styles.valueBlock}>
                <span className={styles.valueLabel}>{researchFieldLabel(field)}</span>
                <ChangeValue value={value} />
              </div>
            ))}
          </div>
        ))
      )}
    </section>
  );
}

export function ResearchSummary({ entry }: { entry: ChangeAuditEntry }) {
  return (
    <>
      <dt>Table</dt>
      <dd>
        {researchTableLabel(entry.entityType ?? "")}{" "}
        <span className={styles.mono}>({entry.entityType})</span>
      </dd>
      <dt>Entity id</dt>
      <dd>
        <span className={styles.mono}>{entry.entityId}</span>
      </dd>
    </>
  );
}

export function ResearchEntryDetail({ entry }: { entry: ChangeAuditEntry }) {
  const changedFields = entry.changedFields ?? {};
  return (
    <>
      <ResearchChanges entry={entry} />
      <CommentarySnapshot changedFields={changedFields} />
      <section aria-labelledby="audit-raw-heading">
        <h3 id="audit-raw-heading" className={styles.sectionHeading}>
          Raw entry
        </h3>
        <pre className={styles.json}>{JSON.stringify(changedFields, null, 2)}</pre>
      </section>
    </>
  );
}
